import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Lock, MessageSquare, Search } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function ForumHomePage() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);

  const { data: cats = [], isLoading } = useQuery({
    queryKey: ["forum-categories"],
    queryFn: () => api.get("/forum/categories").then((r) => r.data),
  });

  const doSearch = async (e) => {
    e.preventDefault();
    if (q.trim().length < 2) return;
    const { data } = await api.get(`/forum/search?q=${encodeURIComponent(q)}`);
    setResults(data.results);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
      <header className="mb-8">
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight">Forum</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Rejoignez la discussion : suggestions, candidatures, signalements et échanges entre joueurs de RiverSide.
        </p>
      </header>

      <form onSubmit={doSearch} className="mb-8 flex gap-2 max-w-xl">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un sujet..."
            className="pl-9"
            data-testid="forum-search-input"
          />
        </div>
        <Button type="submit" data-testid="forum-search-submit">Rechercher</Button>
      </form>

      {results && (
        <div className="mb-10">
          <h2 className="font-display uppercase text-lg mb-3">Résultats ({results.length})</h2>
          {results.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucun sujet trouvé.</p>
          ) : (
            <div className="space-y-2">
              {results.map((t) => (
                <Link key={t.id} to={`/forum/t/${t.id}`} className="block">
                  <Card className="hover:border-primary/60">
                    <CardContent className="p-4 flex justify-between items-center">
                      <div>
                        <p className="font-medium">{t.title}</p>
                        <p className="text-xs text-muted-foreground">Dans {t.category_slug} · par {t.author.persona_name}</p>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
          <Button variant="ghost" size="sm" onClick={() => { setResults(null); setQ(""); }} className="mt-2">Fermer les résultats</Button>
        </div>
      )}

      {isLoading ? (
        <p className="text-muted-foreground">Chargement...</p>
      ) : (
        <div className="grid gap-4">
          {cats.map((c) => (
            <Link key={c.slug} to={`/forum/c/${c.slug}`} data-testid={`forum-cat-${c.slug}`}>
              <Card className="hover:border-primary/60 transition-colors">
                <CardContent className="p-6 flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-display text-xl uppercase">{c.name}</h3>
                      {c.visibility === "private_to_author" && (
                        <Badge variant="outline" className="border-primary/40 text-primary"><Lock className="h-3 w-3 mr-1" /> Privé</Badge>
                      )}
                      {c.post_permission === "staff" && (
                        <Badge variant="outline">Staff uniquement</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{c.description}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-mono-tech text-2xl text-primary">{c.topic_count}</p>
                    <p className="text-xs text-muted-foreground flex items-center justify-end gap-1"><MessageSquare className="h-3 w-3" /> sujets</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
